import { InfoCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { Button } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FromToFilter, usePositionHistory } from "../../../api";
import { Beacon, Position } from "../../../api/models";
import "../../../components/map/Map.css";
import { maxBy } from "../../../util";
import { BeaconTelemetryFilterValue } from "../../BeaconTelemetryFilter";
import { BeaconMapLegend, LegendItem } from "../../map/BeaconMapLegend";
import { getMarkerColorBeacon, getMarkerColorSCBOne } from "../../map/mapUtils";
import { MapDetails } from "./MapDetails";
import { MapDrawer } from "./MapDrawer";
import { formatLocalizedDateTime } from "../../../util/formatting";

export interface SCBOneMapCardProps {
  positions: Position[];
  isLoading: boolean;
  operationRunSelected: boolean;
  selectedOperationRun: string | undefined;
}

export function SCBOneMapCard({
  positions,
  isLoading,
  operationRunSelected,
  selectedOperationRun,
}: SCBOneMapCardProps) {
  const { t } = useTranslation();
  const [mapDrawerVisible, setMapDrawerVisible] = useState(false);
  const [isMapInfoVisible, setIsMapInfoVisible] = useState(false);
  const [updateBounds, setUpdateBounds] = useState(false);
  const prevRunId = useRef<string | undefined>();

  useEffect(() => {
    if (prevRunId.current !== selectedOperationRun) {
      setUpdateBounds(true);
    } else {
      setUpdateBounds(false);
    }

    prevRunId.current = selectedOperationRun;
  }, [positions, selectedOperationRun]);

  const toggleMapInfo = (state: boolean) => {
    setIsMapInfoVisible(state);
  };

  const toggleDrawer = (state: boolean) => {
    setMapDrawerVisible(state);
  };

  /*
    This expects that first item in the operationRunPositions is the end position and last item is the start position. 
    If this does not hold true then will need to take min/max of timestamp to get start and end positions.
    However given the positions array could have 20,000 records, this is not efficient so avoiding implementing
    that for now.
  */
  const markers = () => {
    if (!positions || positions.length === 0) {
      return [];
    }

    return [
      {
        lat: positions[positions.length - 1]?.lat,
        lon: positions[positions.length - 1]?.lon,
        color: getMarkerColorSCBOne(positions[positions.length - 1]),
        title: `Start (${formatLocalizedDateTime(
          positions[positions.length - 1]?.ts
        )})`,
        popUpOnLoad: true,
        onClick: () => {},
      },
      {
        lat: positions[0]?.lat,
        lon: positions[0]?.lon,
        color: getMarkerColorSCBOne(positions[0]),
        title: `End (${formatLocalizedDateTime(positions[0]?.ts)})`,
        popUpOnLoad: true,
        onClick: () => {},
      },
    ];
  };

  const polyLines = [
    {
      line: (positions || []).map((p) => ({
        lat: p.lat,
        lon: p.lon,
      })),
    },
  ];

  return (
    <div
      className="card"
      style={{
        display: "flex",
        flexDirection: "column",
      }}
      data-testid="beacon-map-card"
    >
      <div className="card-header">{t("beacon:position-map")}</div>
      <MapDetails
        isLoading={isLoading}
        markers={markers()}
        polyLines={polyLines}
        alertMessage={
          operationRunSelected
            ? t("scb-one:run-notification-no-data")
            : t("scb-one:run-notification-select")
        }
        updateBounds={updateBounds}
      />
      <MapDrawer
        isLoading={isLoading}
        markers={markers()}
        polyLines={polyLines}
        mapDrawerVisible={mapDrawerVisible}
        onCloseDrawer={() => toggleDrawer(false)}
        mapLegend={LegendItem.SCBOne}
        alertMessage={
          operationRunSelected
            ? t("scb-one:run-notification-no-data")
            : t("scb-one:run-notification-select")
        }
      />
      <div className="beacon-map-card-info-div">
        <Button
          aria-label="map-details-info-button"
          type="default"
          className="info-button"
          onMouseEnter={() => toggleMapInfo(true)}
          onMouseLeave={() => toggleMapInfo(false)}
          icon={<InfoCircleOutlined />}
        />
        <Button
          aria-label="show-drawer-button"
          type="primary"
          onClick={() => toggleDrawer(true)}
          icon={<PlusOutlined />}
        ></Button>
        {isMapInfoVisible && (
          <BeaconMapLegend
            legendItems={[LegendItem.SCBOne]}
            style={{ bottom: "40px" }}
          />
        )}
      </div>
    </div>
  );
}

export interface BeaconMapCardProps {
  beacon: Beacon;
  selectedFromToFilter:
    | (FromToFilter & number)
    | (FromToFilter & {
        from: Date;
        to: Date;
      });
  filter: BeaconTelemetryFilterValue;
}

export function BeaconMapCard({
  beacon,
  selectedFromToFilter,
  filter,
}: BeaconMapCardProps) {
  const { t } = useTranslation();
  const [mapDrawerVisible, setMapDrawerVisible] = useState(false);
  const [isMapInfoVisible, setIsMapInfoVisible] = useState(false);

  const positionHistory = usePositionHistory(
    beacon.serial,
    selectedFromToFilter
  );

  let positions = positionHistory.value?.items || [];

  let newestPosition = maxBy(positionHistory.value?.items || [], (p) =>
    new Date(p.ts).getTime()
  )?.item.position;

  positions = (positions ?? [])?.filter((p) => {
    if (filter === "GpsOnly") return p.position.cprs === 0;
    if (filter === "AccurateCorrectedPosition") return p.position.cprs === 2;
    return true;
  });

  if (filter === "LatestOnly") {
    newestPosition = beacon.position;
  }

  const toggleMapInfo = (state: boolean) => {
    setIsMapInfoVisible(state);
  };

  const toggleDrawer = (state: boolean) => {
    setMapDrawerVisible(state);
  };

  const markers = useMemo(() => {
    if (!beacon.position) {
      return [];
    }

    if (!newestPosition) {
      return [
        {
          lat: beacon.position?.lat || 0,
          lon: beacon.position?.lon || 0,
          color: getMarkerColorBeacon(beacon),
          title: beacon.serial,
          onClick: () => {},
        },
      ];
    }

    return [
      {
        lat: newestPosition?.lat,
        lon: newestPosition?.lon,
        color: getMarkerColorBeacon(beacon),
        title: t("beacon:marker-title", {
          serial: beacon.serial,
          at: formatLocalizedDateTime(positions[0]?.position.ts),
        }),
        onClick: () => {},
      },
    ];
  }, [newestPosition, beacon]);

  const polyLines = useMemo(() => {
    return filter === "LatestOnly"
      ? []
      : [
          {
            line: (positions || []).map((p) => ({
              lat: p.position.lat,
              lon: p.position.lon,
            })),
          },
        ];
  }, [positions, filter]);

  return (
    <div
      className="card"
      style={{
        display: "flex",
        flexDirection: "column",
      }}
      data-testid="beacon-map-card"
    >
      <div className="card-header">{t("beacon:position-map")}</div>
      <MapDetails
        isLoading={positionHistory.loading}
        markers={markers}
        polyLines={polyLines}
      />
      <MapDrawer
        isLoading={positionHistory.loading}
        markers={markers}
        polyLines={polyLines}
        mapDrawerVisible={mapDrawerVisible}
        onCloseDrawer={() => toggleDrawer(false)}
        mapLegend={LegendItem.Markers}
      />
      <div className="beacon-map-card-info-div">
        <Button
          aria-label="map-details-info-button"
          type="default"
          className="info-button"
          onMouseEnter={() => toggleMapInfo(true)}
          onMouseLeave={() => toggleMapInfo(false)}
          icon={<InfoCircleOutlined />}
        />
        <Button
          aria-label="show-drawer-button"
          type="primary"
          onClick={() => toggleDrawer(true)}
          icon={<PlusOutlined />}
        ></Button>
        {isMapInfoVisible && (
          <BeaconMapLegend
            legendItems={[LegendItem.Markers]}
            style={{ bottom: "40px" }}
          />
        )}
      </div>
    </div>
  );
}
